import { ApolloProvider } from '@apollo/client';
import { Auth0Provider } from '@auth0/auth0-react';
import React, { useCallback, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { StaticRouter } from 'react-router-dom/server';
import { graphQLService } from '../../services/graphql-service';
import './../../styles/index.scss';
import { PartsListContext, PartsListsContextProps } from './parts-list-context';
import { QuickOrderContext, QuickOrderContextProps } from './quick-order-context';
import { BaseRouter } from './router/base-router.component';
import { SSRContext, SSRContextType } from './ssr-context';
import { UserProvider } from './user-context';

type AppProps = {
  ssrReq?: any; // Express request
  gqlClient?: any; // Apollo client, optional
  context?: SSRContextType; // context for SSR
};

function App(props: AppProps) {
  const { ssrReq, gqlClient, context } = props;
  const ssrLocation = ssrReq ? ssrReq.url : null;
  const ssrHost = ssrReq?.get('host');

  const [quickOrderProps, setQuickOrderProps] = useState<QuickOrderContextProps | undefined>(undefined);
  const [partsListProps, setPartsListProps] = useState<PartsListsContextProps | undefined>(undefined);

  const setQuickOrderVisible = useCallback((visible: boolean) => {
    setQuickOrderProps(
      (props) =>
        ({
          ...props,
          isVisible: visible,
        } as QuickOrderContextProps)
    );
  }, []);

  // Create SSR-aware router
  const router = ssrLocation ? (
    <StaticRouter location={ssrLocation}>
      <SSRContext.Provider value={context as SSRContextType}>
        <BaseRouter />
      </SSRContext.Provider>
    </StaticRouter>
  ) : (
    <BrowserRouter>
      <BaseRouter />
    </BrowserRouter>
  );

  const apolloProvider = (
    <ApolloProvider client={gqlClient ?? graphQLService.apolloClient}>
      <QuickOrderContext.Provider value={{ quickOrderProps, setQuickOrderProps, setQuickOrderVisible }}>
        <PartsListContext.Provider value={{ partsListProps, setPartsListProps }}>
          <UserProvider>{router}</UserProvider>
        </PartsListContext.Provider>
      </QuickOrderContext.Provider>
    </ApolloProvider>
  );

  // Auth0 wrapper if enabled
  if (process.env.REACT_APP_AUTH_ENABLED === 'true') {
    return (
      <Auth0Provider
        domain={process.env.REACT_APP_AUTH_DOMAIN ?? ''}
        clientId={process.env.REACT_APP_AUTH_CLIENTID ?? ''}
        authorizationParams={{
          redirect_uri: `${typeof window !== 'undefined' ? window.location.origin : ssrHost}/user/login`,
          organization: process.env.REACT_APP_AUTH_ORG_MT,
        }}>
        {apolloProvider}
      </Auth0Provider>
    );
  } else {
    return apolloProvider;
  }
}

export default App;
